import React, { useEffect } from "react";

import { createSyncStoragePersister } from "@tanstack/query-sync-storage-persister";
import { QueryClient } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { hot } from "react-hot-loader";
import { IntlProvider } from "react-intl";
import { BrowserRouter } from "react-router-dom";
import { createGlobalStyle, ThemeProvider } from "styled-components";

import { AuthDependencyProvider } from "./App.dependencies";
import EnvironmentRibbon from "../common/components/EnvironmentRibbon/EnvironmentRibbon";
import Toast from "../common/components/Toast/Toast";
import environment from "../common/environments";
import localization from "../common/localization";
import { base, Theme, ThemeDark, typography } from "../common/styles";
import { bootIntercom } from "../common/util/intercom";
import { AuthContextProvider } from "./context/Auth/Auth.context";
import Router from "./router/Router";

declare global {
  interface Window {
    gtag: any;
    dataLayer: any[];
    Shake: any;
    openInkeep: () => void;
    closeInkeep: () => void;
  }
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      cacheTime: 1000 * 60 * 60 * 24, // 24 hours
    },
  },
});

const localStoragePersister = createSyncStoragePersister({
  storage: window.localStorage,
});

const GlobalStyle = createGlobalStyle<{ theme: Theme }>`
    ${base}
    ${typography}
`;

function App() {
  useEffect(() => {
    bootIntercom(environment.intercomAppId);
  }, []);

  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{ persister: localStoragePersister }}
    >
      <BrowserRouter>
        <ThemeProvider theme={ThemeDark}>
          <GlobalStyle />
          <EnvironmentRibbon />
          <Toast />
          <IntlProvider
            messages={localization["en"]}
            locale="en"
            defaultLocale="en"
          >
            <AuthDependencyProvider>
              <AuthContextProvider>
                <Router />
              </AuthContextProvider>
            </AuthDependencyProvider>
          </IntlProvider>
        </ThemeProvider>
      </BrowserRouter>
    </PersistQueryClientProvider>
  );
}

declare let module: Record<string, unknown>;

export default hot(module)(App);
